import { select } from "d3-selection";
import { type D3ZoomEvent, type ZoomTransform, zoom, zoomIdentity } from "d3-zoom";
import { ChevronDown, ChevronRight, Minimize2, Plus, RotateCcw } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { EmployeeAvatar } from "./EmployeeAvatar";
import type { OrgEmployee } from "./types";

const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;
const HORIZONTAL_GAP = 40;
const VERTICAL_GAP = 60;
const EMPTY_SET: Set<string> = new Set();

type TreeNode = {
  employee: OrgEmployee;
  children: TreeNode[];
};

function buildForest(employees: OrgEmployee[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const e of employees) byId.set(e.id, { employee: e, children: [] });
  const roots: TreeNode[] = [];
  byId.forEach((node) => {
    const managerId = node.employee.directManagerId;
    if (managerId && byId.has(managerId)) {
      byId.get(managerId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });
  byId.forEach((node) => {
    node.children.sort((a: TreeNode, b: TreeNode) =>
      a.employee.fullName.localeCompare(b.employee.fullName),
    );
  });
  return roots;
}

// Returns a copy of `forest` with subtrees pruned at every id present in `collapsedIds`.
// Pruned ancestors retain `children: []` so the chart shows them as leaves.
function pruneCollapsed(forest: TreeNode[], collapsedIds: Set<string>): TreeNode[] {
  const visit = (node: TreeNode): TreeNode => {
    if (collapsedIds.has(node.employee.id)) {
      return { employee: node.employee, children: [] };
    }
    return { employee: node.employee, children: node.children.map(visit) };
  };
  return forest.map(visit);
}

type Axis = "row" | "column";

function computeAxisById(forest: TreeNode[]): Map<string, Axis> {
  const out = new Map<string, Axis>();
  const visit = (node: TreeNode) => {
    if (node.children.length === 0) return;
    const allLeaves = node.children.every((c) => c.children.length === 0);
    out.set(node.employee.id, allLeaves ? "column" : "row");
    node.children.forEach(visit);
  };
  forest.forEach(visit);
  return out;
}

type LaidOutSubtree = {
  width: number;
  height: number;
  positions: Map<string, { x: number; y: number }>;
};

function layoutSubtree(node: TreeNode, axisById: Map<string, Axis>): LaidOutSubtree {
  if (node.children.length === 0) {
    const positions = new Map<string, { x: number; y: number }>();
    positions.set(node.employee.id, { x: NODE_WIDTH / 2, y: NODE_HEIGHT / 2 });
    return { width: NODE_WIDTH, height: NODE_HEIGHT, positions };
  }

  const axis = axisById.get(node.employee.id) ?? "row";
  const childrenRowOffsetY = NODE_HEIGHT + VERTICAL_GAP;

  if (axis === "column") {
    const positions = new Map<string, { x: number; y: number }>();
    const childCount = node.children.length;
    const colHeight = NODE_HEIGHT + childCount * (NODE_HEIGHT + VERTICAL_GAP);
    positions.set(node.employee.id, { x: NODE_WIDTH / 2, y: NODE_HEIGHT / 2 });
    let cy = NODE_HEIGHT + VERTICAL_GAP + NODE_HEIGHT / 2;
    for (const child of node.children) {
      const cl = layoutSubtree(child, axisById);
      cl.positions.forEach((pt, id) => {
        positions.set(id, {
          x: NODE_WIDTH / 2 - cl.width / 2 + pt.x,
          y: cy - NODE_HEIGHT / 2 + pt.y,
        });
      });
      cy += NODE_HEIGHT + VERTICAL_GAP;
    }
    return { width: NODE_WIDTH, height: colHeight, positions };
  }

  const childLayouts = node.children.map((c) => layoutSubtree(c, axisById));
  const totalChildrenWidth =
    childLayouts.reduce((sum, l) => sum + l.width, 0) + HORIZONTAL_GAP * (childLayouts.length - 1);
  const subtreeWidth = Math.max(NODE_WIDTH, totalChildrenWidth);
  const childrenMaxHeight = Math.max(...childLayouts.map((l) => l.height));
  const subtreeHeight = childrenRowOffsetY + childrenMaxHeight;

  const positions = new Map<string, { x: number; y: number }>();
  positions.set(node.employee.id, { x: subtreeWidth / 2, y: NODE_HEIGHT / 2 });

  let cursorX = (subtreeWidth - totalChildrenWidth) / 2;
  for (let i = 0; i < node.children.length; i++) {
    const cl = childLayouts[i];
    cl.positions.forEach((pt, id) => {
      positions.set(id, { x: cursorX + pt.x, y: childrenRowOffsetY + pt.y });
    });
    cursorX += cl.width + HORIZONTAL_GAP;
  }

  return { width: subtreeWidth, height: subtreeHeight, positions };
}

export interface OrgChartViewHandle {
  fitToScreen: () => void;
  focusEmployee: (employeeId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  resetView: () => void;
}

interface OrgChartViewProps {
  employees: OrgEmployee[];
  highlightedIds?: Set<string>;
  onSelect: (employee: OrgEmployee) => void;
  initialFocusId?: string | null;
}

export const OrgChartView = forwardRef<OrgChartViewHandle, OrgChartViewProps>(function OrgChartView(
  { employees, highlightedIds, onSelect, initialFocusId },
  ref,
) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const zoomBehaviorRef = useRef<ReturnType<typeof zoom<SVGSVGElement, unknown>> | null>(null);
  const initialFocusAppliedRef = useRef(false);

  // Manager IDs (ids that have at least one direct report). Computed once per data change.
  const managerIds = useMemo(() => {
    const set = new Set<string>();
    for (const e of employees) {
      if (e.directManagerId) set.add(e.directManagerId);
    }
    return set;
  }, [employees]);

  // Set of manager IDs that are currently collapsed (their subtree is hidden).
  // null = uninitialized — gates the initial focus effect so it doesn't run with
  // stale positions from the un-collapsed render.
  const [collapsedIds, setCollapsedIds] = useState<Set<string> | null>(null);

  // Initial collapse: every manager except roots starts collapsed,
  // so the chart opens showing only the top layer + its direct reports.
  useEffect(() => {
    if (collapsedIds !== null) return;
    if (employees.length === 0) return;
    const rootIds = new Set(employees.filter((e) => !e.directManagerId).map((e) => e.id));
    const initial = new Set<string>();
    managerIds.forEach((id) => {
      if (!rootIds.has(id)) initial.add(id);
    });
    setCollapsedIds(initial);
  }, [employees, managerIds, collapsedIds]);

  const effectiveCollapsed = collapsedIds ?? EMPTY_SET;

  const toggleCollapsed = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const base = prev ?? new Set<string>();
      const next = new Set(base);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const { nodes, links, width, height, positionById } = useMemo(() => {
    const fullForest = buildForest(employees);
    const axisById = computeAxisById(fullForest);
    const prunedForest = pruneCollapsed(fullForest, effectiveCollapsed);

    const rootLayouts = prunedForest.map((r) => layoutSubtree(r, axisById));
    const totalRootsWidth =
      rootLayouts.reduce((sum, l) => sum + l.width, 0) +
      HORIZONTAL_GAP * Math.max(0, rootLayouts.length - 1);
    const padding = 80;
    const w = Math.max(NODE_WIDTH * 2, totalRootsWidth + padding * 2);
    const h = Math.max(
      NODE_HEIGHT * 2,
      Math.max(0, ...rootLayouts.map((l) => l.height)) + padding * 2,
    );

    const positionById = new Map<string, { x: number; y: number }>();
    let rootCursorX = (w - totalRootsWidth) / 2;
    for (let i = 0; i < prunedForest.length; i++) {
      const layout = rootLayouts[i];
      layout.positions.forEach((pt, id) => {
        positionById.set(id, { x: rootCursorX + pt.x, y: padding + pt.y });
      });
      rootCursorX += layout.width + HORIZONTAL_GAP;
    }

    const allNodes: { id: string; employee: OrgEmployee }[] = [];
    const visit = (n: TreeNode) => {
      allNodes.push({ id: n.employee.id, employee: n.employee });
      n.children.forEach(visit);
    };
    prunedForest.forEach(visit);

    const allLinks: { sourceId: string; targetId: string }[] = [];
    const linkVisit = (n: TreeNode) => {
      for (const c of n.children) {
        allLinks.push({ sourceId: n.employee.id, targetId: c.employee.id });
        linkVisit(c);
      }
    };
    prunedForest.forEach(linkVisit);

    return { nodes: allNodes, links: allLinks, width: w, height: h, positionById };
  }, [employees, effectiveCollapsed]);

  const childrenCountById = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of employees) {
      if (e.directManagerId) m.set(e.directManagerId, (m.get(e.directManagerId) ?? 0) + 1);
    }
    return m;
  }, [employees]);

  const fitToScreen = useCallback(() => {
    const svg = svgRef.current;
    const container = containerRef.current;
    const z = zoomBehaviorRef.current;
    if (!svg || !container || !z) return;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    if (cw === 0 || ch === 0) return;
    const scale = Math.min(cw / width, ch / height, 1);
    const tx = (cw - width * scale) / 2;
    const ty = (ch - height * scale) / 2;
    const t = zoomIdentity.translate(tx, ty).scale(scale);
    select(svg).call(z.transform, t);
  }, [width, height]);

  const focusEmployee = useCallback(
    (employeeId: string) => {
      const svg = svgRef.current;
      const container = containerRef.current;
      const z = zoomBehaviorRef.current;
      const pos = positionById.get(employeeId);
      if (!svg || !container || !z || !pos) return;
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const scale = 1;
      const tx = cw / 2 - pos.x * scale;
      const ty = ch / 2 - pos.y * scale;
      const t = zoomIdentity.translate(tx, ty).scale(scale);
      select(svg).call(z.transform, t);
    },
    [positionById],
  );

  const expandAll = useCallback(() => {
    setCollapsedIds(new Set());
  }, []);

  const computeDefaultCollapsed = useCallback(() => {
    const rootIds = new Set(employees.filter((e) => !e.directManagerId).map((e) => e.id));
    const next = new Set<string>();
    managerIds.forEach((id) => {
      if (!rootIds.has(id)) next.add(id);
    });
    return next;
  }, [employees, managerIds]);

  const collapseAll = useCallback(() => {
    setCollapsedIds(computeDefaultCollapsed());
  }, [computeDefaultCollapsed]);

  // "Reset view" — restores the org chart to how it looked on first open:
  // default collapse (only top + 1 layer) + initial focus (centered on viewer if available).
  // Uses a flag picked up by an effect so the focus fires AFTER the new layout settles,
  // otherwise the transform would be computed with stale positions.
  const pendingResetRef = useRef(false);
  const resetView = useCallback(() => {
    setCollapsedIds(computeDefaultCollapsed());
    pendingResetRef.current = true;
  }, [computeDefaultCollapsed]);

  useImperativeHandle(
    ref,
    () => ({ fitToScreen, focusEmployee, expandAll, collapseAll, resetView }),
    [fitToScreen, focusEmployee, expandAll, collapseAll, resetView],
  );

  // Setup zoom behavior once.
  useEffect(() => {
    const svg = svgRef.current;
    const g = gRef.current;
    if (!svg || !g) return;
    const z = zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.2, 2.5])
      .on("zoom", (event: D3ZoomEvent<SVGSVGElement, unknown>) => {
        const t: ZoomTransform = event.transform;
        select(g).attr("transform", `translate(${t.x},${t.y}) scale(${t.k})`);
      });
    zoomBehaviorRef.current = z;
    select(svg).call(z);
    return () => {
      select(svg).on(".zoom", null);
    };
  }, []);

  // After resetView() flips the collapse to its default, this effect catches the flag
  // and re-applies the initial focus using the freshly-laid-out positions.
  useEffect(() => {
    if (!pendingResetRef.current) return;
    if (collapsedIds === null) return;
    pendingResetRef.current = false;
    requestAnimationFrame(() => {
      if (initialFocusId && positionById.has(initialFocusId)) {
        focusEmployee(initialFocusId);
      } else {
        fitToScreen();
      }
    });
  }, [collapsedIds, positionById, initialFocusId, focusEmployee, fitToScreen]);

  // Apply initial focus once nodes are laid out AND the initial collapse state has settled.
  // Gating on collapsedIds !== null prevents focusing with stale positions from the
  // un-collapsed first render (which would otherwise place the transform off-screen).
  useEffect(() => {
    if (initialFocusAppliedRef.current) return;
    if (collapsedIds === null) return;
    if (nodes.length === 0) return;
    initialFocusAppliedRef.current = true;
    requestAnimationFrame(() => {
      if (initialFocusId && positionById.has(initialFocusId)) {
        focusEmployee(initialFocusId);
      } else {
        fitToScreen();
      }
    });
  }, [collapsedIds, nodes.length, initialFocusId, positionById, focusEmployee, fitToScreen]);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full bg-muted/60 dark:bg-muted/20 rounded-md border overflow-hidden"
    >
      <div className="absolute top-3 right-3 z-10 flex gap-2 flex-wrap">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={expandAll}
          data-testid="button-expand-all"
        >
          <Plus className="h-4 w-4 mr-1" /> Expandir tudo
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={collapseAll}
          data-testid="button-collapse-all"
        >
          <Minimize2 className="h-4 w-4 mr-1" /> Recolher tudo
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={resetView}
          data-testid="button-reset-view"
        >
          <RotateCcw className="h-4 w-4 mr-1" /> Reset
        </Button>
      </div>
      <svg ref={svgRef} className="w-full h-full" role="img" aria-label="Organograma">
        <defs>
          <filter id="org-card-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
          </filter>
        </defs>
        <g ref={gRef}>
          {links.map((link) => {
            const sourcePos = positionById.get(link.sourceId);
            const targetPos = positionById.get(link.targetId);
            if (!sourcePos || !targetPos) return null;
            const path = orthogonalLink(
              sourcePos.x,
              sourcePos.y + NODE_HEIGHT / 2,
              targetPos.x,
              targetPos.y - NODE_HEIGHT / 2,
            );
            return (
              <path
                key={`link-${link.sourceId}-${link.targetId}`}
                d={path}
                fill="none"
                stroke="hsl(var(--border))"
                strokeWidth={1.5}
              />
            );
          })}
          {nodes.map(({ id, employee: e }) => {
            const pos = positionById.get(id)!;
            const reportsCount = childrenCountById.get(e.id) ?? 0;
            const dimmed = highlightedIds && highlightedIds.size > 0 && !highlightedIds.has(e.id);
            const highlighted = highlightedIds?.has(e.id);
            const isManager = managerIds.has(e.id);
            const isCollapsed = effectiveCollapsed.has(e.id);
            return (
              <g
                key={e.id}
                transform={`translate(${pos.x - NODE_WIDTH / 2},${pos.y - NODE_HEIGHT / 2})`}
                opacity={dimmed ? 0.35 : 1}
                data-testid={`node-org-${e.id}`}
              >
                {/* biome-ignore lint/a11y/useSemanticElements: <button> isn't a valid SVG child; using <g role="button"> is the standard pattern for clickable SVG nodes. */}
                <g
                  className="cursor-pointer"
                  role="button"
                  tabIndex={-1}
                  aria-label={`Abrir detalhes de ${e.fullName}`}
                  onClick={() => onSelect(e)}
                >
                  <rect
                    width={NODE_WIDTH}
                    height={NODE_HEIGHT}
                    rx={10}
                    className="fill-card"
                    stroke={highlighted ? "hsl(var(--primary))" : "hsl(var(--border))"}
                    strokeWidth={highlighted ? 2 : 1}
                    filter="url(#org-card-shadow)"
                  />
                  <foreignObject x={0} y={0} width={NODE_WIDTH} height={NODE_HEIGHT}>
                    <div className="w-full h-full flex items-center gap-3 px-3">
                      <EmployeeAvatar fullName={e.fullName} photoUrl={e.slackPhotoUrl} size={44} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{e.fullName}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {e.jobTitle ?? "—"}
                        </div>
                      </div>
                    </div>
                  </foreignObject>
                  {reportsCount > 0 && (
                    <g transform={`translate(${NODE_WIDTH - 14}, -8)`}>
                      <circle r={11} className="fill-primary" />
                      <text
                        textAnchor="middle"
                        dy="0.32em"
                        className="fill-primary-foreground text-[10px] font-semibold"
                      >
                        {reportsCount}
                      </text>
                    </g>
                  )}
                </g>
                {isManager && (
                  // biome-ignore lint/a11y/useSemanticElements: <button> isn't a valid SVG child; using <g role="button"> is the standard pattern for clickable SVG nodes.
                  <g
                    transform={`translate(${NODE_WIDTH / 2}, ${NODE_HEIGHT + 10})`}
                    className="cursor-pointer"
                    role="button"
                    tabIndex={-1}
                    aria-label={isCollapsed ? "Expandir reportes" : "Recolher reportes"}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      toggleCollapsed(e.id);
                    }}
                    data-testid={`button-toggle-${e.id}`}
                  >
                    <circle r={11} className="fill-card stroke-border" strokeWidth={1} />
                    <foreignObject x={-11} y={-11} width={22} height={22}>
                      <div className="w-full h-full flex items-center justify-center text-foreground">
                        {isCollapsed ? (
                          <ChevronRight className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                      </div>
                    </foreignObject>
                  </g>
                )}
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
});

function orthogonalLink(x1: number, y1: number, x2: number, y2: number): string {
  const my = (y1 + y2) / 2;
  return `M${x1},${y1} V${my} H${x2} V${y2}`;
}
